import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";

const BG =
  "https://images.unsplash.com/photo-1780643828897-38d8da4887d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxuZXdzJTIwcm9vbSUyMGpvdXJuYWxpc3R8ZW58MHx8fHwxNzgzODM2NzA2fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/command-center" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back.");
      nav("/command-center");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="dark min-h-screen bg-[#0A0A0A] text-zinc-100 font-ui grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={BG} alt="Newsroom" className="absolute inset-0 w-full h-full object-cover opacity-40 grayscale" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="font-tactical font-black text-4xl tracking-tight">Intelligence Command Center</div>
          <p className="mt-3 text-zinc-400 max-w-md">Research, verification and content production for Be-Apuo Media Intelligence.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 text-emerald-400">
            <ShieldCheck className="h-5 w-5" />
            <span className="label-caps">Secure Access</span>
          </div>
          <h1 className="mt-4 font-tactical font-black text-3xl tracking-tight">Sign in</h1>
          <p className="mt-2 text-sm text-zinc-500">Authorized personnel only.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="label-caps text-zinc-500">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full bg-[#121212] border border-zinc-800 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-500 font-mono-data"
                placeholder="james@beapuo.media"
              />
            </div>
            <div>
              <label className="label-caps text-zinc-500">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full bg-[#121212] border border-zinc-800 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-zinc-500 font-mono-data"
                placeholder="••••••••"
              />
            </div>
            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={busy}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm py-2.5 text-sm font-semibold tracking-wide transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Authenticate
            </button>
          </form>
          <Link to="/command-center/reset-password" data-testid="forgot-password-link" className="mt-4 block text-center text-xs text-zinc-500 hover:text-zinc-300">
            Forgot your password?
          </Link>
          <Link to="/" className="mt-3 block text-center text-xs text-zinc-600 hover:text-zinc-400">
            ← Back to public site
          </Link>
        </div>
      </div>
    </div>
  );
}
